import { useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import {
  useConfirmBooking,
  useGetAllBookings,
} from "../../../../api/fleet-management/bookings/booking.hooks";
import { useGetDrivers } from "../../../../api/fleet-management/drivers/drivers.hooks";
import ConfirmBookingModal from "./_components/ConfirmBookingModal";
import BookingsHeader from "./_components/BookingsHeader";
import BookingsTable from "./_components/BookingsTable";
import { filterBookings, normalizeBookings } from "./utils/booking.utils";

const initialConfirmForm = {
  driverId: "",
  overtimeRules: {
    halfDayAfterHours: 2,
    fullDayAfterHours: 6,
    halfDayCharge: 200,
    fullDayCharge: 600,
  },
};

const Bookings = () => {
  const { data, isLoading, isError, error } = useGetAllBookings();
  const { data: driversData, isLoading: isDriversLoading } = useGetDrivers({ status: "available" });
  const { mutate: confirmBooking, isLoading: isConfirmingBooking } = useConfirmBooking();

  const [searchTerm, setSearchTerm] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmForm, setConfirmForm] = useState(initialConfirmForm);

  const bookings = normalizeBookings(data);
  const availableDrivers = Array.isArray(driversData)
    ? driversData
    : driversData?.drivers || driversData?.items || [];

  const filteredBookings = useMemo(() => {
    return filterBookings(bookings, searchTerm);
  }, [bookings, searchTerm]);

  const openConfirmModal = (booking) => {
    setFeedback({ type: "", message: "" });
    setSelectedBooking(booking);
    setConfirmForm({
      driverId: "",
      overtimeRules: {
        halfDayAfterHours: booking?.overtimeRules?.halfDayAfterHours ?? 2,
        fullDayAfterHours: booking?.overtimeRules?.fullDayAfterHours ?? 6,
        halfDayCharge: booking?.overtimeRules?.halfDayCharge ?? 200,
        fullDayCharge: booking?.overtimeRules?.fullDayCharge ?? 600,
      },
    });
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    if (isConfirmingBooking) return;
    setShowConfirmModal(false);
    setSelectedBooking(null);
    setConfirmForm(initialConfirmForm);
  };

  const updateConfirmForm = (key, value, isOvertimeRule = false) => {
    if (!isOvertimeRule) {
      setConfirmForm((prev) => ({ ...prev, [key]: value }));
      return;
    }

    setConfirmForm((prev) => ({
      ...prev,
      overtimeRules: {
        ...prev.overtimeRules,
        [key]: value === "" ? "" : Number(value),
      },
    }));
  };

  const handleConfirmBooking = () => {
    if (!selectedBooking?._id || !confirmForm.driverId) return;

    const payload = {
      driverId: confirmForm.driverId,
      overtimeRules: {
        halfDayAfterHours: Number(confirmForm.overtimeRules.halfDayAfterHours) || 0,
        fullDayAfterHours: Number(confirmForm.overtimeRules.fullDayAfterHours) || 0,
        halfDayCharge: Number(confirmForm.overtimeRules.halfDayCharge) || 0,
        fullDayCharge: Number(confirmForm.overtimeRules.fullDayCharge) || 0,
      },
    };

    confirmBooking(
      { bookingId: selectedBooking._id, payload },
      {
        onSuccess: () => {
          setFeedback({ type: "success", message: "Booking confirmed successfully." });
          closeConfirmModal();
        },
        onError: (confirmError) => {
          setFeedback({
            type: "danger",
            message: confirmError?.response?.data?.message || "Failed to confirm booking.",
          });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load bookings. {error?.response?.data?.message || "Please try again."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <BookingsHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          total={filteredBookings.length}
        />

        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {filteredBookings.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No bookings found</p>
            </div>
          ) : (
            <BookingsTable
              bookings={filteredBookings}
              onConfirm={openConfirmModal}
              isConfirming={isConfirmingBooking}
            />
          )}
        </div>
      </div>

      <ConfirmBookingModal
        show={showConfirmModal}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmBooking}
        booking={selectedBooking}
        form={confirmForm}
        onChange={updateConfirmForm}
        availableDrivers={availableDrivers}
        isLoadingDrivers={isDriversLoading}
        isSubmitting={isConfirmingBooking}
      />
    </div>
  );
};

export default Bookings;